import { AccountAppType } from '../common/enums/account-app-type';
import { PaymentStatus } from '../common/enums/payment-status';
import type { AnonymousPaymentDto } from '../common/dtos/anonymous-payment.dto';
import getAppTypeFromPrefix from './get-app-type-from-prefix';
import dayjs from 'dayjs';

export function isGuestCollectRecipient(
  pageModel?: Pick<AnonymousPaymentDto, 'recipient' | 'anonymousPaymentRecipient'> | null,
): boolean {
  if (!pageModel) {
    return false;
  }

  const recipientEmail = pageModel.recipient?.email ?? '';
  const anonymousEmail = pageModel.anonymousPaymentRecipient?.email ?? '';

  return (
    recipientEmail.includes('guest:') || anonymousEmail.includes('guest:') || !!pageModel.anonymousPaymentRecipient
  );
}

export function clearCollectPaymentAuthLocalStorage(): void {
  localStorage.removeItem('_snap_token');
  localStorage.removeItem('_profile_uid');
  localStorage.removeItem('_owner_id');
  localStorage.removeItem('_user_roles');
  localStorage.removeItem('_origin_app');
  localStorage.removeItem('snap_is_agent');
}

export type GuestCollectLoginResponse = {
  requiresMfa?: boolean;
  challengeToken?: string;
  channel?: 'email' | 'sms';
  destinationHint?: string;
  deviceToken?: string;
  uid?: number;
  stsTokenManager?: { accessToken: string };
};

export type GuestCollectLoginOutcome =
  | {
      kind: 'mfa';
      challengeToken: string;
      channel: 'email' | 'sms';
      destinationHint: string;
      deviceToken?: string;
    }
  | {
      kind: 'session';
      accessToken: string;
      deviceToken?: string;
    }
  | { kind: 'invalid' };

export type CollectPaymentSessionData = {
  id: number;
  profileId: number;
  roles: unknown;
  originApp?: string;
};

/** Relative auth login path for guest collect-payment (claim or agent). */
export function guestCollectLoginPath(appPrefix: string): string {
  const app = (appPrefix || 'claim').trim().toLowerCase() || 'claim';

  return `auth/${app}/login`;
}

export function resolveGuestLoginOutcome(
  response: GuestCollectLoginResponse | null | undefined,
): GuestCollectLoginOutcome {
  if (!response) {
    return { kind: 'invalid' };
  }

  if (response.requiresMfa && response.challengeToken) {
    return {
      kind: 'mfa',
      challengeToken: response.challengeToken,
      channel: response.channel || 'email',
      destinationHint: response.destinationHint || '',
      deviceToken: response.deviceToken,
    };
  }

  const accessToken = response.stsTokenManager?.accessToken;
  if (accessToken) {
    return {
      kind: 'session',
      accessToken,
      deviceToken: response.deviceToken,
    };
  }

  return { kind: 'invalid' };
}

/**
 * Writes collect-payment auth keys after login/MFA. Does not navigate.
 * Prefer calling after setToken + getSession so token/sessionData match nuxt-auth state.
 */
export function applyCollectPaymentSessionLocalStorage(params: {
  accessToken: string;
  bearerToken?: string | null;
  sessionData?: CollectPaymentSessionData | null;
}): void {
  const storedToken = params.bearerToken?.startsWith('Bearer ')
    ? params.bearerToken.split(' ')[1] || params.accessToken
    : params.accessToken;

  localStorage.setItem('_snap_token', storedToken);

  if (!params.sessionData) {
    return;
  }

  localStorage.setItem('_profile_uid', params.sessionData.id.toString());
  localStorage.setItem('_owner_id', params.sessionData.profileId.toString());
  localStorage.setItem('_user_roles', JSON.stringify(params.sessionData.roles));

  if (params.sessionData.originApp) {
    localStorage.setItem('_origin_app', params.sessionData.originApp);
  }
}

export async function clearIncompatibleCollectPaymentAuth(options: {
  appPrefix: string;
  authEmail?: string | null;
  accountAppType?: AccountAppType | null;
  authStatus: string;
  signOut: (options?: { redirect?: boolean }) => Promise<void>;
}): Promise<void> {
  const currentAppType = getAppTypeFromPrefix(options.appPrefix);
  const email = options.authEmail ?? '';
  const isGuestSession = email.includes('guest:');
  const sessionAppType = options.accountAppType ?? null;
  const isWrongPlatform =
    sessionAppType != null && sessionAppType !== AccountAppType.DualApp && sessionAppType !== currentAppType;

  if (options.authStatus === 'authenticated' && (isGuestSession || isWrongPlatform)) {
    clearCollectPaymentAuthLocalStorage();
    await options.signOut({ redirect: false });
  }
}

export const COLLECT_PAYMENT_LOAD_ERROR_MESSAGE = 'Invalid link, please request a new one from the sender';

export function extractApiErrorMessage(data: {
  message?: string | string[] | { message?: string };
}): string | undefined {
  const { message } = data;
  if (typeof message === 'string') {
    return message;
  }
  if (Array.isArray(message)) {
    return message.join('.\n');
  }
  if (message && typeof message === 'object' && typeof message.message === 'string') {
    return message.message;
  }
  return undefined;
}

export function getCollectPaymentStatusMessage(payment: {
  status: string | null;
  type?: string | null;
  failureReason?: string | null;
  estimatedSettlementAt?: string | Date | null;
}): string | null {
  const { status } = payment;
  if (!status || status === PaymentStatus.AwaitingSignUp || status === PaymentStatus.AwaitingCashOut) {
    return null;
  }

  if (status === PaymentStatus.Settled) {
    if (payment.type?.includes('Paper Check')) {
      return 'This payment has been mailed as a paper check.';
    }
    return 'This payment has already been completed';
  }

  if (status === PaymentStatus.Cancelled) {
    return 'This payment was cancelled — please contact the sender for a new payment';
  }

  if (status === PaymentStatus.Failed) {
    return `This payment was unsuccessful. ${payment.failureReason ? `The failure reason was ${payment.failureReason}.` : ''} Please contact the sender for further inquiry.`;
  }

  if (status === PaymentStatus.InTransit || status === PaymentStatus.PreparingToMail) {
    if (!payment.estimatedSettlementAt) {
      return 'This payment has been cashed out and is being processed.';
    }

    const isPaperCheck = payment.type?.includes('Paper Check');
    const estimate = dayjs(payment.estimatedSettlementAt).format('MM/DD/YYYY');

    if (status === PaymentStatus.InTransit) {
      return isPaperCheck
        ? `This payment has been cashed out. It is currently in transit via ${payment.type} and should arrive by ${estimate}`
        : `This payment has been cashed out. It is currently processing via ${payment.type} and should be deposited by ${estimate}`;
    }

    return isPaperCheck
      ? `This payment has been cashed out. It is currently being prepared to mail and should arrive by ${estimate}`
      : `This payment has been cashed out. It is currently being processed and should be deposited by ${estimate}`;
  }

  return 'This payment has been cashed out.';
}
